<script setup lang="ts">
import type { ScaleConfig } from "@/types/legend.types";
import { computed } from "vue";
import { getFontForFileName } from "@/config/fonts";

const props = defineProps<{
  scale: ScaleConfig;
  /** Hauteur forcée en vigueur : elle remplace celle du style. */
  forcedSize?: number | "";
}>();

const size = computed<number | undefined>(
  () => props.scale.props.textsize ?? props.scale.props.textstyle?.size,
);

/** Police lisible plutôt que le nom du fichier .shx/.ttf. */
const font = computed<string | undefined>(() => {
  const textstyle = props.scale.props.textstyle;
  if (!textstyle?.filename) return textstyle?.name;
  return getFontForFileName(textstyle.filename)?.name ?? textstyle.name;
});

/** La hauteur du style ne s'applique plus : on la barre au profit de la forcée. */
const overriddenSize = computed<number | null>(() => props.forcedSize || null);
</script>
<template>
  <div class="scale-row">
    <span class="scale-name text-truncate">{{ scale.name }}</span>
    <span v-if="font" class="scale-font text-truncate">{{ font }}</span>
    <span v-if="size" class="scale-size">
      <span :class="{ 'size-overridden': overriddenSize }">{{ size }}</span>
      <span v-if="overriddenSize" class="forced-size">
        → {{ overriddenSize }}
      </span>
    </span>
  </div>
</template>

<style lang="scss" scoped>
.scale-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.scale-name {
  flex: 1;
  min-width: 0;
}

// Police et hauteur en fin de ligne : ce sont des détails du style, pas son nom
.scale-font {
  max-width: 45%;
  opacity: 0.6;
  font-size: 11px;
}

.scale-size {
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  opacity: 0.8;
  font-size: 11px;
}

.size-overridden {
  text-decoration: line-through;
  opacity: 0.6;
}

.forced-size {
  color: var(--canvas-forced);
  white-space: nowrap;
}
</style>
